import { makeStyles } from "tss-react/mui";
import { formatDate, getRootUrl } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

export const MiPrintHeader = ({
  account = {},
  headerFields = [],
  highlightedHeaderField = null,
  statusHeaderField = null,
  showPrintedDate = false,
}) => {
  const API_URL = getRootUrl();
  const { classes, theme } = useStyles();

  const {
    legalName,
    phoneNumber,
    facsimileNumber,
    website,
    email,
    address,
    logo,
  } = account;

  const renderHeaderFields = () =>
    headerFields.map(({ label, value }) => (
      <Box key={label} className={classes.column}>
        <Typography className={classes.headerSubtitle} fontSize="inherit">
          {label}
        </Typography>
        <Typography fontSize="inherit" color={theme.palette.text.primary}>
          {value}
        </Typography>
      </Box>
    ));

  return (
    <Box className={classes.header}>
      <Box className={classes.headerSection}>
        {logo && (
          <img
            src={`${API_URL}/${logo?.referenceId}`}
            alt="Company Logo"
            className={classes.headerLogo}
          />
        )}
        <Typography
          fontSize={theme.typography.pxToRem(13)}
          fontWeight="inherit"
        >
          {address?.addressLine1 && (
            <>
              {address?.addressLine1}
              <br />
            </>
          )}
          {`${address?.city || ""}${address?.city ? ", " : ""}${
            address?.regionCode || ""
          } ${address?.postalCode || ""}`}
        </Typography>
      </Box>
      <Box className={classes.headerSection} flex={1}>
        <Box className={classes.headerNameSection}>
          <Box className={classes.headerUnderline}>
            <Typography className={classes.headerTitle}>
              {legalName || ""}
            </Typography>
            {highlightedHeaderField && (
              <Box className={classes.headerHighlightedField}>
                <Typography className={classes.hightlightedFieldLabel}>
                  {highlightedHeaderField.label}
                </Typography>
                <Typography className={classes.hightlightedFieldValue}>
                  {highlightedHeaderField.value}
                </Typography>
              </Box>
            )}
            {statusHeaderField && (
              <Box display="flex" alignItems="center" mb={1}>
                <Typography
                  fontSize="inherit"
                  color={theme.palette.common.black}
                  fontWeight={theme.fontWeight.semiBold}
                >
                  {statusHeaderField.label}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginLeft: theme.spacing(1),
                    padding: theme.spacing(0.5, 1),
                    borderRadius: theme.spacing(0.5),
                    backgroundColor: `${statusHeaderField.value?.color}2A`,
                    border: `1px solid ${statusHeaderField.value?.color}`,
                    color: statusHeaderField.value?.color,
                  }}
                >
                  <Typography fontSize="inherit">
                    {statusHeaderField.value?.status}
                  </Typography>
                </Box>
              </Box>
            )}
            {showPrintedDate && (
              <Box display="flex" alignItems="center" mt={1.75} mb={1} mr={2}>
                <Typography fontSize="inherit">Printed Date:</Typography>
                <Typography fontSize="inherit">
                  {formatDate(new Date(), "MM/DD/YYYY")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box className={classes.headerRow}>
          <Box className={classes.headerInformationSection}>
            <Typography fontSize="inherit" fontWeight="inherit">
              {website && (
                <>
                  {website}
                  <br />
                </>
              )}
              {email || ""}
            </Typography>
            <Typography fontSize="inherit" fontWeight="inherit">
              {phoneNumber && (
                <>
                  {`Phone: ${phoneNumber}`}
                  <br />
                </>
              )}
              {facsimileNumber ? `Fax: ${facsimileNumber}` : null}
            </Typography>
          </Box>
          {headerFields.length ? (
            <Box className={classes.headerFieldsSection}>
              {renderHeaderFields()}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  header: {
    display: "flex",
    paddingLeft: theme.spacing(1.5),
    gap: theme.spacing(4),
  },
  headerSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.fontWeight.regular,
    color: theme.palette.grey.medium,
  },
  headerTitle: {
    height: 19,
    color: theme.palette.common.black,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    lineHeight: theme.typography.pxToRem(13),
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.fontWeight.semiBold,
  },
  headerUnderline: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    borderBottom: `1px solid ${theme.palette.vibrant.light_blue_a50}`,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  headerNameSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerInformationSection: {
    display: "flex",
    gap: theme.spacing(3),
  },
  headerFieldsSection: {
    display: "flex",
    gap: theme.spacing(3),
    paddingRight: theme.spacing(2),
  },
  headerSubtitle: {
    color: theme.palette.common.black,
    fontWeight: theme.fontWeight.semiBold,
  },
  headerLogo: {
    height: 32,
  },
  headerHighlightedField: {
    border: `1px solid ${theme.palette.blue.dark}`,
    borderRadius: theme.typography.pxToRem(4),
    backgroundColor: theme.palette.blue.light,
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 0.7, 1),
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  hightlightedFieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.blue.main,
  },
  hightlightedFieldValue: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.fontWeight.bold,
    color: theme.palette.blue.main,
  },
  column: {
    display: "flex",
    flexDirection: "column",
  },
}));
